'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RoiCalculator() {
  const t = useTranslations("tools.roiCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    invested: 1000,
    returned: 2000,
    timeType: 'dates' as 'dates' | 'length',
    startDate: '2021-01-01',
    endDate: new Date().toISOString().split('T')[0],
    investmentLength: 2.5,
  });

  const [results, setResults] = useState<{
    gain: number;
    roi: number;
    annualizedROI: number;
    years: number;
    investedPercent: number;
    profitPercent: number;
  } | null>(null);

  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const calculateYearsBetweenDates = (startDate: string, endDate: string): number => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays / 365.25;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { invested, returned, timeType, startDate, endDate, investmentLength } = inputs;

    if (invested <= 0) {
      setError(t('errorInvestedPositive'));
      return;
    }

    if (returned < 0) {
      setError(t('errorReturnedNegative'));
      return;
    }

    let years: number;

    if (timeType === 'dates') {
      if (!startDate || !endDate) {
        setError(t('errorSelectDates'));
        return;
      }

      if (new Date(endDate) <= new Date(startDate)) {
        setError(t('errorEndDateAfterStart'));
        return;
      }

      years = calculateYearsBetweenDates(startDate, endDate);
    } else {
      if (investmentLength <= 0) {
        setError(t('errorLengthPositive'));
        return;
      }
      years = investmentLength;
    }

    const gain = returned - invested;
    const roi = (gain / invested) * 100;
    const annualizedROI = (Math.pow(returned / invested, 1 / years) - 1) * 100;

    const investedPercent = returned > 0 ? (invested / returned) * 100 : 100;
    const profitPercent = returned > 0 ? (gain / returned) * 100 : 0;

    setResults({
      gain,
      roi,
      annualizedROI,
      years,
      investedPercent,
      profitPercent,
    });
  };

  const formatCurrency = (value: number): string => {
    return '$' + value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatPercentage = (value: number): string => {
    return value.toFixed(2) + '%';
  };

  const createPieChartPath = (startAngle: number, endAngle: number): string => {
    const radius = 80;
    const centerX = 100;
    const centerY = 100;

    const polarToCartesian = (angleInDegrees: number) => {
      const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians)),
      };
    };

    const start = polarToCartesian(endAngle);
    const end = polarToCartesian(startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M", centerX, centerY,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#10b981',
    error: '#ef4444',
    blue: '#2563eb',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    summaryText: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
    },
    toolHeader: {
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    calculatorLayout: {
      display: 'grid',
      gap: '2rem',
      alignItems: 'start',
    },
    calculatorForm: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontWeight: 600,
      color: '#334155',
      marginBottom: '0.5rem',
      fontSize: '0.95rem',
    },
    inputWrapper: {
      position: 'relative' as const,
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      paddingLeft: '2rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    inputPrefix: {
      position: 'absolute' as const,
      left: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 600,
    },
    timeOptions: {
      background: '#f8fafc',
      padding: '1.25rem',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
    },
    timeOptionsTitle: {
      fontWeight: 600,
      color: '#334155',
      marginBottom: '1rem',
      fontSize: '0.95rem',
    },
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
      marginBottom: '1rem',
    },
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
    },
    radioInput: {
      marginRight: '0.5rem',
      cursor: 'pointer',
      width: '18px',
      height: '18px',
    },
    radioLabel: {
      margin: 0,
      cursor: 'pointer',
      fontWeight: 500,
      color: '#475569',
    },
    calculateButton: {
      width: '100%',
      padding: '1rem',
      background: `linear-gradient(135deg, ${colors.blue} 0%, #1d4ed8 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontSize: '1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
    },
    results: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
    },
    resultItemHighlight: {
      background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
      border: `2px solid ${colors.success}`,
    },
    resultLabel: {
      fontWeight: 600,
      color: '#475569',
      fontSize: '0.95rem',
    },
    resultValue: {
      fontSize: '1.25rem',
      fontWeight: 700,
      color: colors.success,
    },
    chartContainer: {
      marginTop: '2rem',
      textAlign: 'center' as const,
    },
    chartLegend: {
      display: 'flex',
      justifyContent: 'center',
      gap: '2rem',
      marginTop: '1.5rem',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    legendColor: {
      width: '20px',
      height: '20px',
      borderRadius: '4px',
    },
    legendText: {
      fontSize: '0.9rem',
      color: '#475569',
      fontWeight: 500,
    },
    errorMessage: {
      background: '#fef2f2',
      border: `2px solid ${colors.error}`,
      color: '#991b1b',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    list: {
      paddingLeft: '1.5rem',
      marginBottom: '1rem',
    },
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
    },
    reference: {
      marginBottom: '0.75rem',
    },
    link: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    baseStyles.toolContainer,
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '1000px' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    baseStyles.calculatorLayout,
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    baseStyles.calculatorForm,
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsStyle = getResponsiveStyle(
    baseStyles.results,
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const radioGroupStyle = getResponsiveStyle(
    baseStyles.radioGroup,
    { flexDirection: 'column' as const, gap: '0.75rem' },
    { flexDirection: 'row' as const, gap: '1.5rem' },
    { flexDirection: 'row' as const, gap: '1.5rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={baseStyles.toolHeader}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={calculatorFormStyle}>
              {error && (
                <div style={baseStyles.errorMessage}>
                  {error}
                </div>
              )}

              <form onSubmit={handleCalculate}>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="invested">
                    {t('amountInvestedLabel')}
                  </label>
                  <div style={baseStyles.inputWrapper}>
                    <span style={baseStyles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="invested"
                      step="0.01"
                      min="0"
                      value={inputs.invested}
                      onChange={(e) => setInputs({ ...inputs, invested: parseFloat(e.target.value) || 0 })}
                      style={baseStyles.input}
                      required
                    />
                  </div>
                </div>

                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="returned">
                    {t('amountReturnedLabel')}
                  </label>
                  <div style={baseStyles.inputWrapper}>
                    <span style={baseStyles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="returned"
                      step="0.01"
                      min="0"
                      value={inputs.returned}
                      onChange={(e) => setInputs({ ...inputs, returned: parseFloat(e.target.value) || 0 })}
                      style={baseStyles.input}
                      required
                    />
                  </div>
                </div>

                <div style={baseStyles.timeOptions}>
                  <div style={baseStyles.timeOptionsTitle}>{t('investmentTimeLabel')}</div>

                  <div style={radioGroupStyle}>
                    <div style={baseStyles.radioOption}>
                      <input
                        type="radio"
                        id="time-dates"
                        name="time-type"
                        value="dates"
                        checked={inputs.timeType === 'dates'}
                        onChange={() => setInputs({ ...inputs, timeType: 'dates' })}
                        style={baseStyles.radioInput}
                      />
                      <label style={baseStyles.radioLabel} htmlFor="time-dates">
                        {t('useDatesOption')}
                      </label>
                    </div>
                    <div style={baseStyles.radioOption}>
                      <input
                        type="radio"
                        id="time-length"
                        name="time-type"
                        value="length"
                        checked={inputs.timeType === 'length'}
                        onChange={() => setInputs({ ...inputs, timeType: 'length' })}
                        style={baseStyles.radioInput}
                      />
                      <label style={baseStyles.radioLabel} htmlFor="time-length">
                        {t('useLengthOption')}
                      </label>
                    </div>
                  </div>

                  {inputs.timeType === 'dates' && (
                    <div>
                      <div style={baseStyles.formGroup}>
                        <label style={baseStyles.label} htmlFor="start-date">
                          {t('fromDateLabel')}
                        </label>
                        <input
                          type="date"
                          id="start-date"
                          value={inputs.startDate}
                          onChange={(e) => setInputs({ ...inputs, startDate: e.target.value })}
                          style={{ ...baseStyles.input, paddingLeft: '0.75rem' }}
                        />
                      </div>
                      <div style={baseStyles.formGroup}>
                        <label style={baseStyles.label} htmlFor="end-date">
                          {t('toDateLabel')}
                        </label>
                        <input
                          type="date"
                          id="end-date"
                          value={inputs.endDate}
                          onChange={(e) => setInputs({ ...inputs, endDate: e.target.value })}
                          style={{ ...baseStyles.input, paddingLeft: '0.75rem' }}
                        />
                      </div>
                    </div>
                  )}

                  {inputs.timeType === 'length' && (
                    <div style={baseStyles.formGroup}>
                      <label style={baseStyles.label} htmlFor="investment-length">
                        {t('investmentLengthLabel')}
                      </label>
                      <div style={baseStyles.inputWrapper}>
                        <input
                          type="number"
                          id="investment-length"
                          step="0.01"
                          min="0"
                          value={inputs.investmentLength}
                          onChange={(e) => setInputs({ ...inputs, investmentLength: parseFloat(e.target.value) || 0 })}
                          style={{ ...baseStyles.input, paddingLeft: '0.75rem', paddingRight: '4rem' }}
                        />
                        <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500, fontSize: '0.9rem' }}>
                          {t('yearsUnit')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  style={baseStyles.calculateButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(37, 99, 235, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(37, 99, 235, 0.3)';
                  }}
                >
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            {results && (
              <div style={resultsStyle}>
                <h3 style={h2Style}>{t('resultsTitle')}</h3>

                <div style={baseStyles.resultItem}>
                  <span style={baseStyles.resultLabel}>{t('investmentGainLabel')}</span>
                  <span style={{ ...baseStyles.resultValue, color: results.gain >= 0 ? colors.success : colors.error }}>
                    {formatCurrency(results.gain)}
                  </span>
                </div>

                <div style={{ ...baseStyles.resultItem, ...baseStyles.resultItemHighlight }}>
                  <span style={baseStyles.resultLabel}>{t('roiLabel')}</span>
                  <span style={{ ...baseStyles.resultValue, fontSize: '1.5rem', color: results.roi >= 0 ? colors.success : colors.error }}>
                    {formatPercentage(results.roi)}
                  </span>
                </div>

                <div style={baseStyles.resultItem}>
                  <span style={baseStyles.resultLabel}>{t('annualizedRoiLabel')}</span>
                  <span style={{ ...baseStyles.resultValue, color: results.annualizedROI >= 0 ? colors.success : colors.error }}>
                    {formatPercentage(results.annualizedROI)}
                  </span>
                </div>

                <div style={baseStyles.resultItem}>
                  <span style={baseStyles.resultLabel}>{t('investmentLengthResultLabel')}</span>
                  <span style={{ ...baseStyles.resultValue, color: '#475569' }}>
                    {results.years.toFixed(3)} {t('yearsUnit')}
                  </span>
                </div>

                <div style={baseStyles.chartContainer}>
                  <svg width="200" height="200" viewBox="0 0 200 200">
                    <circle cx="100" cy="100" r="90" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2"/>

                    {results.investedPercent > 0 && (
                      <path
                        d={createPieChartPath(0, (results.investedPercent / 100) * 360)}
                        fill={colors.blue}
                        stroke="white"
                        strokeWidth="2"
                      />
                    )}

                    {results.profitPercent > 0 && (
                      <path
                        d={createPieChartPath((results.investedPercent / 100) * 360, ((results.investedPercent + results.profitPercent) / 100) * 360)}
                        fill={colors.success}
                        stroke="white"
                        strokeWidth="2"
                      />
                    )}

                    <circle cx="100" cy="100" r="35" fill="white"/>
                  </svg>

                  <div style={baseStyles.chartLegend}>
                    <div style={baseStyles.legendItem}>
                      <div style={{ ...baseStyles.legendColor, background: colors.blue }}></div>
                      <span style={baseStyles.legendText}>{t('investedLegend')}</span>
                    </div>
                    <div style={baseStyles.legendItem}>
                      <div style={{ ...baseStyles.legendColor, background: colors.success }}></div>
                      <span style={baseStyles.legendText}>{t('profitLegend')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('howToUseStep1')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep2')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep3')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep4')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('insightsBullet1')}</li>
          <li style={baseStyles.listItem}>{t('insightsBullet2')}</li>
          <li style={baseStyles.listItem}>{t('insightsBullet3')}</li>
          <li style={baseStyles.listItem}>{t('insightsBullet4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('detailsFormulaTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsFormulaParagraph')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('detailsAnnualizedTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsAnnualizedParagraph')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('detailsBestPracticesTitle')}</h3>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('detailsBestPractice1')}</li>
          <li style={baseStyles.listItem}>{t('detailsBestPractice2')}</li>
          <li style={baseStyles.listItem}>{t('detailsBestPractice3')}</li>
          <li style={baseStyles.listItem}>{t('detailsBestPractice4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faqQuestion1')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer1')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faqQuestion2')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer2')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faqQuestion3')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer3')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faqQuestion4')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer4')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faqQuestion5')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer5')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={baseStyles.reference}>
          <a href="https://valuecore.ai/glossary/roi-calculator/" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            ROI CALCULATOR - Valuecore
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a href="https://www.bdc.ca/en/articles-tools/entrepreneur-toolkit/financial-tools/return-on-total-assets" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            Return on Investment (ROI) Calculator - BDC
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a href="https://www.omnicalculator.com/finance/roi" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            ROI Calculator - Omni Calculator
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a href="https://www.salesforce.com/blog/what-is-roi/" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            What is Return on Investment (ROI)? Meaning and Formulas - Salesforce
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a href="https://quickbooks.intuit.com/ca/resources/tools/roi-calculator/" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            Return on Investment (ROI) Calculator - QuickBooks
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a href="https://www.everafter.ai/glossary/roi-calculator" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            What Is an ROI Calculator? - EverAfter
          </a>
        </div>
      </section>
    </div>
  );
}